import { ResponseOption } from '@focaldata/cin-ui-components'

import { EntryType } from './common'
import { LogicClauseProposition } from './questionLogic'

export interface RenderedQuestionnaire {
  surveyId: string
  questionnaire: Questionnaire
  quotaStatus: QuotaStatus
}

export interface QuotaStatus {
  quotas: Quota[]
}

export interface Quota {
  key: QuotaKey
  isFull: boolean
}

export interface QuotaKey {
  questionId: string
  responseOptionId: string
}

export interface Questionnaire {
  questionnaireId: string
  entries: QuestionnaireEntry[]
  locale: {
    language: string
    country: string
  }
}

export interface QuotaResponseOption {
  responseOptionId: string
  questionId: string
}

export enum QuestionKind {
  AudienceKind = 'AudienceKind',
  QuestionnaireKind = 'QuestionnaireKind'
}

export interface QuestionnaireEntry {
  entryNumber: number
  entryPosition: number
  renderedEntryPosition: number
  contextPosition: number
  renderedContextPosition: number
  entryType: EntryType
  entryItem: MatrixItem | QuestionItem | TextCardItem
  renderedForks?: RenderedForkTag[]
  questionKind: QuestionKind
}

export interface RenderedForkTag {
  forkName: string
  branchLabel: string
}
export interface MatrixItem {
  matrixTitle: {
    title: string
    titleStyling?: string
    matrixTitleId: string
  }
  matrixRows: MatrixRow[]
  responseOptions: EntryResponseOption[]
  settingValues: EntrySettingValue[]
  questionLogic?: LogicClauseProposition[][]
}

export interface ScaleRange {
  min: number
  max: number
  step: number
  startingPosition: number
}
export interface QuestionScaleLabel {
  label: {
    text: string
  }
  position: number
}

export interface QuestionScale {
  range: ScaleRange
  labels: QuestionScaleLabel[]
}

export interface QuestionItem {
  questionTypeCode: string
  question: Question
  questionImageUrl?: string
  questionMedia?: QuestionMedia
  responseOptions?: EntryResponseOption[]
  settingValues?: EntrySettingValue[]
  questionScale?: QuestionScale
  questionLogic?: LogicClauseProposition[][]
}

export interface QuestionMedia {
  mediaUrl: string
  mediaType: MediaType
}

export enum MediaType {
  Image = 'Image',
  Video = 'Video'
}
export interface TextCardItem {
  textCard: TextCard
  questionLogic?: LogicClauseProposition[][]
  questionMedia: QuestionMedia
}

export interface TextCard {
  textCardId: string
  title: string
  titleStyling?: string
  subtitle: string
  body: string
}

export interface MatrixRow {
  position: number
  renderedPosition: number
  question: Question
  maskingRules: MaskingRule[]
}

export interface EntryResponseOption {
  position: number
  option: ResponseOption['option']
  renderedPosition: number
  route?: Route
  maskingRules: MaskingRule[]
}

export interface MaskingRule {
  clause: MaskingRuleClause
  maskingKeys: MaskingKey[]
}

export enum MaskingRuleClause {
  AND = 'AND',
  CONTAINS = 'CONTAINS',
  IS_ONE_OF = 'IS_ONE_OF',
  OR = 'OR'
}

export interface MaskingKey {
  questionId: string
  responseOptionId: string
  matrixTitleId?: string
}

export interface EntrySettingValue {
  code: SettingCodes
  value: SettingValues
}

export interface Route {
  targetNumber: number
}

export interface Question {
  questionId: string
  text: string
  titleStyling?: string
  textStyling?: string
}

export enum RoType {
  RoDouble,
  RoInt,
  RoLink,
  RoString
}

export enum QuestionTypes {
  FreeText = 'free_text',
  Ranked = 'ranked',
  Scale = 'scale',
  BasicChoice = 'basic',
  StandardAudience = 'standard_audience',
  CustomAudience = 'custom_audience'
}

export enum SettingCodes {
  CharacterLimit = 'character_limit',
  ChoiceLimit = 'choice_limit',
  ChoiceIntervalMin = 'choice_interval_min',
  ChoiceIntervalMax = 'choice_interval_max',
  BasicChoice = 'basic_choice',
  ChoiceRequisite = 'choice_requisite',
  Routing = 'routing',
  Quotas = 'quotas',
  ContentStructure = 'content_structure',
  SliderLabels = 'slider_labels'
}

export enum SettingValues {
  Enabled = 'enabled',
  Disabled = 'disabled',
  SingleChoice = 'single_choice',
  MultipleChoice = 'multiple_choice',
  Numeric1 = 'numeric__^-?[0-9]\\d*(\\.\\d+)?$',
  Numeric2 = 'numeric_posint__^\\d+$',
  Numeric3 = 'numeric_int__^-?\\d+$',
  PostCode = 'postcode_uk__^[a-zA-Z]{1,2}\\d[a-zA-Z\\d]?\\s*\\d[a-zA-Z]{2}$',
  Alphanumeric = 'alphanumeric__^[A-Za-z0-9\\s]+$',
  Email = 'email__^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$',
  Unstructured = 'unstructured',
  PostCodeFull = 'postcode_uk__full'
}
